// ── Control Plane Context ──
// Provides resolved capabilities, identity admin, simulation, and decision helpers.

import { createContext, useContext, useState, useMemo, useCallback, useEffect, ReactNode } from "react";
import type {
  ResolvedCapability, ResolutionContext, ModeProfileId,
  Environment, BehaviorMode, ControlPlaneAuditEntry, AuditActionType,
  ManagedUser, ManagedTenant, ManagedRole, UserAccountStatus, TenantStatus,
  DecisionSummary, ImpactPreview, ChangeRequest, SimulationResult,
} from "./types";
import type { UserRole } from "@/data/RoleContext";
import {
  resolveCapabilities, canUseCapability, getCapabilityMode, getCapabilityInfluence,
  getEffectiveConfig, explainDecision, simulateChange, computeImpact,
} from "./engine";
import { useRole } from "@/data/RoleContext";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import { buildManagedRoles } from "./seedAdmin";
import { ControlPlaneService } from "@/services/control-plane.service";

interface ControlPlaneContextValue {
  // Capability resolution
  resolved: ResolvedCapability[];
  environment: Environment;
  modeProfile: ModeProfileId;
  auditLog: ControlPlaneAuditEntry[];

  // Decision helpers
  canUse: (capabilityId: string) => boolean;
  getMode: (capabilityId: string) => BehaviorMode;
  getInfluence: (capabilityId: string) => { ui: boolean; intelligence: boolean; recommendations: boolean; financials: boolean };
  effectiveConfig: Record<string, { enabled: boolean; mode: BehaviorMode }>;

  // Explainability
  explain: (capabilityId: string) => DecisionSummary;

  // Simulation
  simulate: (change: ChangeRequest) => SimulationResult;
  previewImpact: (change: ChangeRequest) => ImpactPreview;

  // Resolution context (for simulation engine)
  resolutionContext: ResolutionContext;

  // Capability admin
  setEnvironment: (env: Environment) => void;
  setModeProfile: (profile: ModeProfileId) => void;
  setOverride: (capabilityId: string, enabled: boolean, mode: BehaviorMode, reason: string) => void;
  clearOverride: (capabilityId: string) => void;

  // User admin
  users: ManagedUser[];
  updateUserStatus: (userId: string, status: UserAccountStatus, reason: string) => void;
  updateUserRole: (userId: string, role: UserRole, reason: string) => void;
  setUserCapabilityOverride: (userId: string, capabilityId: string, enabled: boolean, mode: BehaviorMode, reason: string) => void;
  clearUserCapabilityOverride: (userId: string, capabilityId: string) => void;
  resolveForUser: (userId: string) => ResolvedCapability[];

  // Tenant admin
  tenants: ManagedTenant[];
  updateTenantStatus: (tenantId: string, status: TenantStatus, reason: string) => void;
  updateTenantProfile: (tenantId: string, profile: ModeProfileId, reason: string) => void;
  setTenantCapabilityOverride: (tenantId: string, capabilityId: string, enabled: boolean, mode: BehaviorMode, reason: string) => void;

  // Role admin
  roles: ManagedRole[];
}

const ControlPlaneContext = createContext<ControlPlaneContextValue | undefined>(undefined);

function auditId(): string {
  return `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ControlPlaneProvider({ children }: { children: ReactNode }) {
  const { role: currentRole } = useRole();
  const [environment, setEnvironmentState] = useState<Environment>("internal");
  const [modeProfile, setModeProfileState] = useState<ModeProfileId>("internal");
  const [overrides, setOverrides] = useState<Record<string, Partial<Pick<ResolvedCapability, "enabled"> & { behaviorMode: BehaviorMode }>>>({});
  const [auditLog, setAuditLog] = useState<ControlPlaneAuditEntry[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [tenants, setTenants] = useState<ManagedTenant[]>([]);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  const roles = useMemo(() => buildManagedRoles(users), [users]);

  // ── Load from DB on mount ───────────────────────────────────────────────────
  useEffect(() => {
    if (deferHeavyProvider) return;

    Promise.all([
      ControlPlaneService.loadSystemSettings(),
      ControlPlaneService.loadOverrides(),
      ControlPlaneService.loadAuditLog(),
      ControlPlaneService.loadUsers(),
      ControlPlaneService.loadTenants(),
    ]).then(([settings, savedOverrides, auditEntries, savedUsers, savedTenants]) => {
      if (settings) {
        setEnvironmentState(settings.environment);
        setModeProfileState(settings.modeProfile);
      }
      if (Object.keys(savedOverrides).length > 0) setOverrides(savedOverrides);
      if (auditEntries.length > 0) setAuditLog(auditEntries);
      if (savedUsers.length > 0) setUsers(savedUsers);
      if (savedTenants.length > 0) setTenants(savedTenants);
    });
  }, [deferHeavyProvider]);

  const addAudit = useCallback((
    actionType: AuditActionType, targetType: ControlPlaneAuditEntry["targetType"],
    targetId: string, oldValue: Record<string, unknown>, newValue: Record<string, unknown>,
    reason: string, scope: ControlPlaneAuditEntry["scope"], capabilityId?: string,
  ) => {
    const entry: ControlPlaneAuditEntry = {
      id: auditId(), timestamp: new Date().toISOString(),
      actor: "current-user", actorRole: currentRole,
      actionType, targetType, targetId, capabilityId,
      oldValue, newValue, reason, scope,
    };
    setAuditLog(prev => [entry, ...prev]);
    ControlPlaneService.saveAuditEntry(entry);
  }, [currentRole]);

  const ctx: ResolutionContext = useMemo(() => ({
    environment,
    tenantId: "koredor-primary",
    userId: "current-user",
    userRole: currentRole,
    modeProfile,
    overrides,
  }), [environment, currentRole, modeProfile, overrides]);

  const resolved = useMemo(() => resolveCapabilities(ctx), [ctx]);
  const effectiveConfig = useMemo(() => getEffectiveConfig(resolved), [resolved]);

  const canUse = useCallback((id: string) => canUseCapability(resolved, id), [resolved]);
  const getMode = useCallback((id: string) => getCapabilityMode(resolved, id), [resolved]);
  const getInfluence = useCallback((id: string) => getCapabilityInfluence(resolved, id), [resolved]);

  // Explainability
  const explain = useCallback((capabilityId: string): DecisionSummary => {
    const cap = resolved.find(r => r.id === capabilityId);
    if (!cap) return {
      capabilityId, finalState: "hidden", finalReason: "Not found", primaryDecidingPass: "N/A",
      dependencyIssues: [], conflictIssues: [], roleRestriction: null, tenantInfluence: null,
      userOverride: null, explanation: "Capability not found in registry.",
    };
    return explainDecision(cap);
  }, [resolved]);

  // Simulation
  const simulate = useCallback((change: ChangeRequest): SimulationResult => {
    return simulateChange(change, ctx, resolved, users, tenants);
  }, [ctx, resolved, users, tenants]);

  const previewImpact = useCallback((change: ChangeRequest): ImpactPreview => {
    const sim = simulateChange(change, ctx, resolved, users, tenants);
    return sim.impactPreview;
  }, [ctx, resolved, users, tenants]);

  // Capability Admin
  const setEnvironment = useCallback((env: Environment) => {
    addAudit("environment_change", "system", "environment",
      { environment }, { environment: env }, `Environment changed to ${env}`, "global");
    setEnvironmentState(env);
    ControlPlaneService.saveSystemSettings(env, modeProfile);
  }, [environment, modeProfile, addAudit]);

  const setModeProfile = useCallback((profile: ModeProfileId) => {
    addAudit("profile_change", "system", "profile",
      { modeProfile }, { modeProfile: profile }, `Profile changed to ${profile}`, "global");
    setModeProfileState(profile);
    ControlPlaneService.saveSystemSettings(environment, profile);
  }, [environment, modeProfile, addAudit]);

  const setOverride = useCallback((capabilityId: string, enabled: boolean, mode: BehaviorMode, reason: string) => {
    setOverrides(prev => {
      const old = prev[capabilityId];
      addAudit("capability_override", "capability", capabilityId,
        { enabled: old?.enabled ?? true, mode: old?.behaviorMode ?? "active" },
        { enabled, mode }, reason, "global", capabilityId);
      ControlPlaneService.upsertOverride(capabilityId, enabled, mode);
      return { ...prev, [capabilityId]: { enabled, behaviorMode: mode } };
    });
  }, [addAudit]);

  const clearOverride = useCallback((capabilityId: string) => {
    setOverrides(prev => { const next = { ...prev }; delete next[capabilityId]; return next; });
    ControlPlaneService.deleteOverride(capabilityId);
  }, []);

  // User Admin
  const updateUserStatus = useCallback((userId: string, status: UserAccountStatus, reason: string) => {
    setUsers(prev => prev.map(u => {
      if (u.id !== userId) return u;
      addAudit("user_status_change", "user", userId,
        { status: u.status }, { status }, reason, "user");
      const updated = { ...u, status, updatedAt: new Date().toISOString() };
      ControlPlaneService.upsertUser(updated);
      return updated;
    }));
  }, [addAudit]);

  const updateUserRole = useCallback((userId: string, role: UserRole, reason: string) => {
    setUsers(prev => prev.map(u => {
      if (u.id !== userId) return u;
      addAudit("user_role_change", "user", userId,
        { role: u.role }, { role }, reason, "user");
      const updated = { ...u, role, updatedAt: new Date().toISOString() };
      ControlPlaneService.upsertUser(updated);
      return updated;
    }));
  }, [addAudit]);

  const setUserCapabilityOverride = useCallback((userId: string, capabilityId: string, enabled: boolean, mode: BehaviorMode, reason: string) => {
    setUsers(prev => prev.map(u => {
      if (u.id !== userId) return u;
      const old = u.capabilityOverrides[capabilityId];
      addAudit("user_capability_override", "user", userId,
        { enabled: old?.enabled ?? true, mode: old?.behaviorMode ?? "active" },
        { enabled, mode }, reason, "user", capabilityId);
      const updated = {
        ...u,
        capabilityOverrides: { ...u.capabilityOverrides, [capabilityId]: { enabled, behaviorMode: mode } },
        updatedAt: new Date().toISOString(),
      };
      ControlPlaneService.upsertUser(updated);
      return updated;
    }));
  }, [addAudit]);

  const clearUserCapabilityOverride = useCallback((userId: string, capabilityId: string) => {
    setUsers(prev => prev.map(u => {
      if (u.id !== userId) return u;
      const next = { ...u.capabilityOverrides };
      delete next[capabilityId];
      const updated = { ...u, capabilityOverrides: next, updatedAt: new Date().toISOString() };
      ControlPlaneService.upsertUser(updated);
      return updated;
    }));
  }, []);

  const resolveForUser = useCallback((userId: string): ResolvedCapability[] => {
    const user = users.find(u => u.id === userId);
    if (!user) return [];
    const tenant = tenants.find(t => t.id === user.tenantId);
    const userCtx: ResolutionContext = {
      environment: tenant?.environment ?? environment,
      tenantId: user.tenantId,
      userId: user.id,
      userRole: user.role,
      modeProfile: tenant?.modeProfile ?? modeProfile,
      overrides: user.capabilityOverrides,
      tenantOverrides: tenant?.capabilityOverrides ?? {},
    };
    return resolveCapabilities(userCtx);
  }, [users, tenants, environment, modeProfile]);

  // Tenant Admin
  const updateTenantStatus = useCallback((tenantId: string, status: TenantStatus, reason: string) => {
    setTenants(prev => prev.map(t => {
      if (t.id !== tenantId) return t;
      addAudit("tenant_status_change", "tenant", tenantId,
        { status: t.status }, { status }, reason, "tenant");
      const updated = { ...t, status, updatedAt: new Date().toISOString() };
      ControlPlaneService.upsertTenant(updated);
      return updated;
    }));
  }, [addAudit]);

  const updateTenantProfile = useCallback((tenantId: string, profile: ModeProfileId, reason: string) => {
    setTenants(prev => prev.map(t => {
      if (t.id !== tenantId) return t;
      addAudit("tenant_profile_change", "tenant", tenantId,
        { modeProfile: t.modeProfile }, { modeProfile: profile }, reason, "tenant");
      const updated = { ...t, modeProfile: profile, updatedAt: new Date().toISOString() };
      ControlPlaneService.upsertTenant(updated);
      return updated;
    }));
  }, [addAudit]);

  const setTenantCapabilityOverride = useCallback((tenantId: string, capabilityId: string, enabled: boolean, mode: BehaviorMode, reason: string) => {
    setTenants(prev => prev.map(t => {
      if (t.id !== tenantId) return t;
      addAudit("tenant_capability_override", "tenant", tenantId,
        {}, { enabled, mode }, reason, "tenant", capabilityId);
      const updated = {
        ...t,
        capabilityOverrides: { ...t.capabilityOverrides, [capabilityId]: { enabled, behaviorMode: mode } },
        updatedAt: new Date().toISOString(),
      };
      ControlPlaneService.upsertTenant(updated);
      return updated;
    }));
  }, [addAudit]);

  const value: ControlPlaneContextValue = useMemo(() => ({
    resolved, environment, modeProfile, auditLog,
    canUse, getMode, getInfluence, effectiveConfig,
    explain, simulate, previewImpact, resolutionContext: ctx,
    setEnvironment, setModeProfile, setOverride, clearOverride,
    users, updateUserStatus, updateUserRole, setUserCapabilityOverride, clearUserCapabilityOverride, resolveForUser,
    tenants, updateTenantStatus, updateTenantProfile, setTenantCapabilityOverride,
    roles,
  }), [
    resolved, environment, modeProfile, auditLog, canUse, getMode, getInfluence, effectiveConfig,
    explain, simulate, previewImpact, ctx,
    setEnvironment, setModeProfile, setOverride, clearOverride,
    users, updateUserStatus, updateUserRole, setUserCapabilityOverride, clearUserCapabilityOverride, resolveForUser,
    tenants, updateTenantStatus, updateTenantProfile, setTenantCapabilityOverride,
    roles,
  ]);

  return (
    <ControlPlaneContext.Provider value={value}>
      {children}
    </ControlPlaneContext.Provider>
  );
}

export function useControlPlane(): ControlPlaneContextValue {
  const ctx = useContext(ControlPlaneContext);
  if (!ctx) throw new Error("useControlPlane must be used within ControlPlaneProvider");
  return ctx;
}
